import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import {combineLatest, Observable} from 'rxjs';
import { DataElement, Region, Template, TemplateCode } from '@store/user/user.model';
import { selectSelectedGeos, selectTemplate } from '@store/user/user.selector';
import { debounceTime, filter, map, tap } from 'rxjs/operators';

import * as SearchBoardActions from '@store/search-board/search-board.actions';
import {
  GetSearchResultsCount,
  SetShowSelected,
  SaveValuesOfQuickSearch, SaveValuesOfMySearch, ClearSearchResultsCount
} from '@store/search-board/search-board.actions';
import { DISPLAY_FIELDS, TOTAL_RECORDS, FieldCode } from '@root/search/models/display-fields.constant';
import { OperatorValue, SEARCH_RESULTS_COUNT_DEBOUNCE_TIME, SearchField, RENDER_ID } from '@store/search-board/search-board.model';
import { RlstValidators } from '@shared/validators/validators';
import { selectQuickSearchFieldsData } from '@store/search-board/search-board.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { RemoveCurrentPropertyIndex } from '@store/reports/reports.actions';

@UntilDestroy()
@Component({
  selector: 'rlst-quick-search',
  templateUrl: './quick-search.component.html',
  styleUrls: ['./quick-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuickSearchComponent implements OnInit, OnDestroy {
  @Output() clearSearchResults: EventEmitter<void> = new EventEmitter<void>();
  public quickSearchForm: UntypedFormGroup;
  public template$: Observable<Template>;
  public isFormEmpty: boolean = false;
  private currentCounties$: Observable<Array<Region>>;
  private countyList: Array<string>;
  private isDashboardSearch: boolean;
  /** Map of fieldCode to DataElement for multifield detection */
  private dataElementMap: Record<string, DataElement> = {};

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly store$: Store<AppState>,
    private readonly mixpanelService: MixpanelService
  ) { }

  ngOnInit() {
    const state = history.state;
    let address = '';
    if (state && state.isDashboardSearch) {
      this.isDashboardSearch = true;
      this.clearSearchResults.emit();
      address = state.address;
      history.replaceState({
        isDashboardSearch: false,
        address: ''
      }, '');
    }
    this.createForm();
    this.template$ = combineLatest([
      this.store$.pipe(select(selectTemplate(TemplateCode.TMPL_QUICK_SEARCH))),
      this.store$.pipe(select(selectQuickSearchFieldsData))
    ])
      .pipe(
        tap(([template, searchFields]: [Template, Array<SearchField>]) => {
          // Build map for multifield detection
          this.dataElementMap = {};
          template.dataElementList.forEach((dataElement: DataElement) => {
            this.dataElementMap[dataElement.fieldCode] = dataElement;
            // Avoid repeatedly resetting controls on every store emission.
            if (this.quickSearchForm.get(dataElement.fieldCode)) {
              return;
            }

            const data = searchFields &&
              searchFields.find((field: SearchField) => field.fieldCode === dataElement.fieldCode);
            const formArray: Array<UntypedFormGroup> = [];

            if (data) {
              data.operatorValues.forEach((operatorValue: OperatorValue) => {
                let values = operatorValue.values;
                if (dataElement.fieldCode === 'SITE_ADDRESS' && address) {
                  values = [address, ''];
                  address = null;
                }
                formArray.push(this.createFormGroup(operatorValue.operator, values, dataElement))
              });
            } else {
              let value: Array<string | number> | string | number =
                dataElement.operatorValueList && dataElement.operatorValueList.length && dataElement.operatorValueList[0] ?
                  dataElement.operatorValueList[0].values : '';
              if (dataElement.fieldCode === 'SITE_ADDRESS' && address) {
                value = address;
              }
              formArray.push(this.createFormGroup(dataElement.defaultOperator, [value, ''], dataElement));
            }

            this.quickSearchForm.setControl(dataElement.fieldCode, this.fb.array(formArray));
          });
        }),
        map(([template, _]: [Template, Array<SearchField>]) => template)
      )

    this.store$.select(selectSelectedGeos)
      .pipe(untilDestroyed(this))
      .subscribe((result) => this.countyList = result.map((x) => x.fips));
  }

  ngOnDestroy(): void {
    this.store$.dispatch(new SearchBoardActions.SaveValuesOfQuickSearch(this.getSearchFields(true)));
  }

  public search(): void {
    this.store$.dispatch(new SetShowSelected(false));
    const searchFields = this.filterEmptyValues(this.getSearchFields());
    this.store$.dispatch(new SearchBoardActions.QuickSearch({
      searchFields,
      displayFields: DISPLAY_FIELDS,
      totalRecords: TOTAL_RECORDS
    }));
    this.store$.dispatch(new RemoveCurrentPropertyIndex());

    this.mixpanelService.track('Search - Search Executed', {
      searchType: 'quickSearch',
      isSavedSearch: false,
      searchParameters: searchFields.map((x) => x.fieldCode),
      countyList: this.countyList,
      isApiSearch: false
    });

    this.store$.dispatch(new SaveValuesOfMySearch([]));  // reset MySearchFields
    this.store$.dispatch(new SaveValuesOfQuickSearch(searchFields));
  }

  public getSearchFields(forSave: boolean = false): Array<SearchField> {
    return Object
      .keys(this.quickSearchForm.value)
      .map((fieldCode: string) => {
        const operatorValues = this.quickSearchForm.value[fieldCode]
          .map((operatorValue: OperatorValue) => {
            const values = [];
            if (operatorValue.values['valueFrom']) {
              if (fieldCode === FieldCode.OWNER_NAME_1 && operatorValue.values['valueFrom'].includes(',') && !forSave) {
                const valueArray = operatorValue.values['valueFrom'].split(',');
                const firstName = valueArray[1].trim();
                const lastName = valueArray[0].trim();
                values.push(`${lastName} ${firstName}`);
              } else {
                values.push(operatorValue.values['valueFrom']);
              }
            } else {
              values.push('');
            }
            if (operatorValue.values['valueTo']) {
              values.push(operatorValue.values['valueTo']);
            }
            return { ...operatorValue, values: values };
          });
        return {
          fieldCode,
          operatorValues
        };
      });
  }

  public clearSearch(event: Event): void {
    event.preventDefault();
    this.clearSearchResults.emit();
    this.clearValuesOnly();
  }

  /**
   * Clears only the value fields (valueFrom, valueTo) for all search fields, preserving operator selections.
   * For multifield/multiselect, sets to []; for others, sets to ''.
   */
  private clearValuesOnly(): void {
    Object.keys(this.quickSearchForm.controls).forEach(fieldCode => {
      const fieldArray = this.quickSearchForm.get(fieldCode) as UntypedFormArray;
      const isMulti = this.isMultiField(fieldCode);
      if (fieldArray && fieldArray.controls) {
        fieldArray.controls.forEach((group: UntypedFormGroup) => {
          const valuesGroup = group.get('values') as UntypedFormGroup;
          if (valuesGroup) {
            const valueFromControl = valuesGroup.get('valueFrom');
            const valueToControl = valuesGroup.get('valueTo');
            if (valueFromControl) {
              valueFromControl.setValue(isMulti ? [] : '');
              valueFromControl.markAsPristine();
              valueFromControl.markAsUntouched();
            }
            if (valueToControl) {
              valueToControl.setValue(isMulti ? [] : '');
              valueToControl.markAsPristine();
              valueToControl.markAsUntouched();
            }
          }
        });
      }
    });
    this.quickSearchForm.markAsPristine();
    this.quickSearchForm.markAsUntouched();
  }

  /**
   * Returns true if the field is a multifield/multiselect (e.g., by renderId or config).
   */
  private isMultiField(fieldCode: string): boolean {
    const dataElement = this.dataElementMap[fieldCode];
    if (!dataElement || !dataElement.displayInfo) return false;
    const renderId = dataElement.displayInfo.renderId;
    // Add more RENDER_IDs here if you have other multifield/multiselect types
    return renderId === RENDER_ID.MULTI_SELECT_SEARCH_LOOKUP;
  }

  public onFieldAdded(control: AbstractControl, searchField: DataElement): void {
    const valueFromControl = control.get(['values', 'valueFrom']);
    const valueToControl = control.get(['values', 'valueTo']);
    const searchList: UntypedFormArray = this.quickSearchForm.get(searchField.fieldCode) as UntypedFormArray;
    searchList.push(new UntypedFormGroup({
      operator: new UntypedFormControl(control.get('operator').value),
      values: new UntypedFormGroup({
        valueFrom: new UntypedFormControl(valueFromControl.value,
          RlstValidators.getSingleFieldValidator(searchField.validationInfoList || [])),
        valueTo: new UntypedFormControl(valueToControl.value,
          RlstValidators.getSingleFieldValidator(searchField.validationInfoList || []))
      }, RlstValidators.getValidators(searchField.validationInfoList || [], control.get('operator').value))
    }));
    valueFromControl.setValue('');
    valueToControl.setValue('');
    this.mixpanelService.track('Search - Or Field Added', {
      searchType: 'quickSearch',
      fieldName: searchField.fieldCode
    });
  }

  public onOperatorValueChanged(control: AbstractControl, dataElement: DataElement): void {
    const valueToControl: AbstractControl = control.get(['values', 'valueTo']);
    if (control.get('operator').value === 'BETWEEN') {
      valueToControl.setValidators(
        RlstValidators.getSingleFieldValidator(dataElement.validationInfoList || []).concat(Validators.required));
    } else {
      valueToControl.setValidators(
        RlstValidators.getSingleFieldValidator(dataElement.validationInfoList || []));
    }
  }

  private createForm(): void {
    this.quickSearchForm = this.fb.group({});
    this.currentCounties$ = this.store$.pipe(select(selectSelectedGeos));
    this.store$.dispatch(new ClearSearchResultsCount());

    combineLatest([
      this.currentCounties$,
      this.quickSearchForm.valueChanges
    ])
      .pipe(
        map((_) => {
          return this.filterEmptyValues(this.getSearchFields()).filter((searchField) => {
            return !!searchField.operatorValues[0].values &&
              !!(searchField.operatorValues[0].values[0] || searchField.operatorValues[0].values[1]);
          });
        }),
        tap((fields: Array<SearchField>) => this.isFormEmpty = fields.length <= 0),
        debounceTime(SEARCH_RESULTS_COUNT_DEBOUNCE_TIME),
        filter((searchFields: Array<SearchField>) => searchFields && searchFields.length > 0),
        untilDestroyed(this)
      )
      .subscribe((searchFields) => {
        this.store$.dispatch(new GetSearchResultsCount(searchFields));
        if(this.isDashboardSearch) {
          this.isDashboardSearch = false;
          this.search();
        }
      });
  }

  private createFormGroup(operator: string,
    values: Array<Array<string | number> | string | number>,
    dataElement: DataElement): UntypedFormGroup {
    return this.fb.group({
      operator,
      values: this.fb.group(
        {
          valueFrom: [values[0] || '', RlstValidators.getSingleFieldValidator(dataElement.validationInfoList || [])],
          valueTo: [values[1] || '', RlstValidators.getSingleFieldValidator(dataElement.validationInfoList || [])]
        }, RlstValidators.getValidators(dataElement.validationInfoList || [], operator))
    });
  }

  private filterEmptyValues(searchFields: Array<SearchField>): Array<SearchField> {
    return searchFields.reduce((filteredSearchFields: Array<SearchField>, field: SearchField) => {
      const filteredOperatorValues = field.operatorValues.filter((operatorValue: OperatorValue) =>
        !!operatorValue.values[0] || !!operatorValue.values[1]
      );
      if (filteredOperatorValues.length) {
        filteredSearchFields.push({
          ...field,
          operatorValues: filteredOperatorValues
        });
      }
      return filteredSearchFields;
    }, []);
  }
}
